import './OrderTableGroup.style.less'
import { Table } from 'antd'
import type { TableRowSelection } from 'antd/es/table/interface'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'
import { useAtom } from 'jotai'
import { themeAtom } from '@/jotai/theme.jotai'
import { isDark } from '@/utils/darkmode'
import { useEffect, useState } from 'react'
import { useInfiniteQuery } from 'react-query'
import { getGroupOrders, getPropertyOrders } from '../../OrderTable.service'
import { massUpdateAtom, orderIdAtom } from '@/screens/Order/jotai/order.jotai'
import { nestedOrderColumns } from '../../OrderTable.transform'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import { uniqBy } from 'lodash'
import { Tile } from '@/screens/Order/Pages/Landing/PBFLandingPage'
import { useAuth } from '@/providers/auth.provider'

interface Group {
  value: string
  key: string
  name: string
  data_type: string
}

interface OrderSelection {
  key: string
  properties: string[]
  orders: any[]
}

function OrderTableGroup(props: {
  groupData: any
  display: any
  sortBy: Tile
  loading: boolean
  dataOrderColumns: any
  hideActions?: any
}) {
  const { t } = useTranslation()
  const [theme] = useAtom(themeAtom)
  const [, setMassUpdate] = useAtom(massUpdateAtom)

  const [selectedRowKeys, setselectedRowKeys] = useState([])
  const [, setOrderId] = useAtom(orderIdAtom)
  const [groupOrders, setGroupOrders] = useState([])
  const [group, setGroup] = useState<Group | undefined>()
  const [orders, setOrders] = useState<OrderSelection>({
    key: '',
    properties: [],
    orders: [],
  })

  const { user } = useAuth()

  useEffect(() => {
    setOrders({
      key: '',
      properties: [],
      orders: [],
    })
    setselectedRowKeys([])
    setMassUpdate(true)
  }, [props.groupData])

  const {
    data: groupData,
    isFetching,
    isLoading,
  } = useInfiniteQuery(
    ['orderGroupExpandQuery', group?.value, props.sortBy],
    () => {
      const groupByCookie = localStorage.getItem('groupBy')
      let payload: any = {
        // page_number: pageParam,
        page_size: 200,
        value: group?.value !== 'Blank' ? group?.value : '',
        data_type: group?.data_type,
        group: groupByCookie,
      }
      if (props.sortBy) {
        payload = { ...payload, sortBy: props.sortBy }
      }
      return getGroupOrders(payload)
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.data.next_page <= lastPage.data.total_page
          ? lastPage.data.next_page
          : undefined
      },
    }
  )

  useEffect(() => {
    if (props.groupData?.data?.data) {
      const values = props.groupData.data.data.map((order) => ({
        ...order,
        children: [],
      }))
      setGroupOrders(values)
    }
  }, [props.groupData])

  useEffect(() => {
    if (group && groupData?.pages) {
      groupData.pages.map((page) => {
        if (page.data?.data) {
          const values = groupOrders.map((order) => {
            if (order?.value === group?.value) {
              const next = page.data?.data.map((data) => ({
                ...data,
                value: `${data.id}`,
              }))

              return {
                ...order,
                children: order?.children
                  ? uniqBy([...order.children, ...next], 'id')
                  : next,
              }
            }

            return order
          })

          setGroupOrders(values)
        }
      })
    }
  }, [group, groupData])

  useEffect(() => {
    setOrderId(orders)
  }, [orders])

  const rowSelection: TableRowSelection<any> = {
    onChange: (selectedRowKeys, selectedRows) => {
      setselectedRowKeys(selectedRowKeys)
      setMassUpdate(selectedRows.length ? false : true)
    },
    onSelect: async (record, selected, selectedRows) => {
      if (record?.key) {
        if (selected) {
          let containExistingOwnChild = false
          orders.orders.forEach((item) => {
            if (record?.children.some((data) => data.id == item)) {
              containExistingOwnChild = true
            }
          })
          setOrders((prev) => ({
            key: record.key,
            properties: [...prev.properties, record.value],
            orders: containExistingOwnChild ? [] : [...prev.orders],
          }))
          containExistingOwnChild = false
        } else {
          const filteredProperties = orders.properties.filter(
            (property) => property !== record.value
          )
          setOrders({
            key: record.key,
            properties: filteredProperties,
            orders: [],
          })
        }
      } else {
        // Select sub order item
        if (selected) {
          setOrders((prev) => ({
            key: prev.key ?? '',
            properties: [...prev.properties],
            orders: [...prev.orders, record.id],
          }))
        } else {
          const property = selectedRows
            .filter((item) => item.key)
            .map((item) => item.value)

          const removedProperty: any = orders.properties.filter(
            (item) => !property.includes(item)
          )

          let ordersFromRemovedValue = []

          for (const index in removedProperty) {
            const { data } = await getPropertyOrders({
              key: orders.key,
              value:
                removedProperty[index] != 'Blank' ? removedProperty[index] : '',
              data_type: 'text',
            })

            ordersFromRemovedValue = [...ordersFromRemovedValue, ...data.data]
          }

          const exisitingOrders = ordersFromRemovedValue.filter(
            (item) => item.id != record.id
          )

          setOrders((prev) => ({
            key: prev.key,
            properties: property,
            orders: [...exisitingOrders.map((item) => item.id)].concat(
              ...prev.orders.filter((order) => order !== record.id)
            ),
          }))
        }
      }
      setGroup(record)
      setMassUpdate(false)
    },
    onSelectAll: async (selected, selectedRows) => {
      const properties = selectedRows.filter((item) => item.key)
      if (selected) {
        const filteredProperties = properties.map((row) => row.value)
        setOrders({
          key: selectedRows[0].key,
          properties: filteredProperties,
          orders: [],
        })
      } else {
        setOrders({ key: '', properties: [], orders: [] })
      }
    },

    selectedRowKeys: selectedRowKeys,
  }

  return (
    <div
      className={cx(
        isDark(theme) && 'dark-table',
        'table-container order-group-container table-left'
      )}
    >
      <Table
        rowKey="value"
        sticky
        columns={nestedOrderColumns(
          props.display,
          t,
          user,
          props.dataOrderColumns || [],
          props.hideActions
        )}
        loading={props.loading || isFetching || isLoading}
        rowSelection={{ ...rowSelection, checkStrictly: false }}
        dataSource={groupOrders}
        pagination={false}
        scroll={{
          x: 'max-content',
        }}
        tableLayout="auto"
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) =>
            record?.children &&
            (expanded ? (
              <DownOutlined
                style={{ marginRight: 16, verticalAlign: 'top' }}
                onClick={(e) => onExpand(record, e)}
              />
            ) : (
              <RightOutlined
                style={{ marginRight: 16, verticalAlign: 'top' }}
                onClick={(e) => onExpand(record, e)}
              />
            )),
          onExpand(expanded, record) {
            setGroup(record)
          },
        }}
      />
    </div>
  )
}

export default OrderTableGroup
